import { useState } from 'react';
import { Sparkles, Check, RotateCcw, Palette, AlertTriangle } from 'lucide-react';
import { aiApi } from '../../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../../components/Modal';

interface ThemeSuggestion {
  name: string;
  background_color: string;
  text_color: string;
  font_family: string;
}

interface AIThemeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (theme: ThemeSuggestion) => void;
}

type Step = 'input' | 'loading' | 'preview';

const PRESETS = [
  {
    id: 'christmas',
    icon: '🎄',
    title: 'Natal',
    description: 'Cores natalinas e festivas',
    prompt: 'Crie temas para uma roda de prêmios de Natal, com cores vermelho, verde e dourado. Estilo festivo e elegante.',
  },
  {
    id: 'blackfriday',
    icon: '🖤',
    title: 'Black Friday',
    description: 'Visual impactante para promoções',
    prompt: 'Crie temas para uma roda de prêmios de Black Friday. Cores escuras com detalhes vibrantes. Visual moderno e impactante.',
  },
  {
    id: 'tropical',
    icon: '🌴',
    title: 'Tropical / Verão',
    description: 'Cores vibrantes e tropicais',
    prompt: 'Crie temas tropicais para uma roda de prêmios de verão. Cores vibrantes como laranja, turquesa, amarelo. Visual alegre.',
  },
  {
    id: 'elegant-dark',
    icon: '🌙',
    title: 'Elegante Escuro',
    description: 'Sofisticado e premium',
    prompt: 'Crie temas escuros e elegantes para uma roda de prêmios premium. Cores como preto, dourado e prata. Visual sofisticado.',
  },
  {
    id: 'minimal',
    icon: '✨',
    title: 'Minimalista',
    description: 'Clean e moderno',
    prompt: 'Crie temas minimalistas e limpos para uma roda de prêmios. Cores neutras com toques sutis de cor. Visual clean e profissional.',
  },
  {
    id: 'valentines',
    icon: '💝',
    title: 'Dia dos Namorados',
    description: 'Romântico e apaixonado',
    prompt: 'Crie temas românticos para uma roda de prêmios do Dia dos Namorados. Tons de rosa, vermelho e branco. Visual delicado e elegante.',
  },
];

export default function AIThemeModal({ isOpen, onClose, onSelect }: AIThemeModalProps) {
  const [step, setStep] = useState<Step>('input');
  const [prompt, setPrompt] = useState('');
  const [suggestions, setSuggestions] = useState<ThemeSuggestion[]>([]);
  const [error, setError] = useState('');

  const resetModal = () => {
    setStep('input');
    setPrompt('');
    setSuggestions([]);
    setError('');
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Digite uma descrição para o tema');
      return;
    }

    setStep('loading');
    setError('');

    try {
      const response = await aiApi.generateThemes(prompt);
      if (response.success && response.themes) {
        setSuggestions(response.themes);
        setStep('preview');
      }
    } catch (err: any) {
      setError(err.error || 'Erro ao gerar temas. Tente novamente.');
      setStep('input');
      toast.error(err.error || 'Erro ao gerar temas com IA');
    }
  };

  const handleSelectTheme = (theme: ThemeSuggestion) => {
    onSelect(theme);
    toast.success(`Tema "${theme.name}" aplicado!`);
    handleClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Gerar Tema com IA" size="lg">
      <div className="space-y-4">
        {/* Step: Input */}
        {step === 'input' && (
          <>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-tinta mb-3">
                Escolha um estilo ou descreva seu tema ideal
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {PRESETS.map((preset) => {
                  const selected = prompt === preset.prompt;
                  return (
                    <button
                      key={preset.id}
                      onClick={() => setPrompt(preset.prompt)}
                      className={`group relative rounded-lg border p-3 text-left transition-all ${
                        selected
                          ? 'border-2 border-bordo bg-bordo/8'
                          : 'border-tinta/15 bg-white hover:border-bordo/40 hover:bg-bordo/5'
                      }`}
                    >
                      <div className="flex items-start gap-2.5">
                        <span className="text-xl flex-shrink-0">{preset.icon}</span>
                        <div className="min-w-0">
                          <h4 className={`text-xs font-bold transition-colors ${selected ? 'text-bordo' : 'text-tinta group-hover:text-bordo'}`}>
                            {preset.title}
                          </h4>
                          <p className="text-[10px] text-tinta-soft mt-0.5 line-clamp-2 italic">
                            {preset.description}
                          </p>
                        </div>
                      </div>
                      {selected && (
                        <div className="absolute top-1.5 right-1.5">
                          <Check className="h-3.5 w-3.5 text-bordo" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Ex: Tema com cores da marca Coca-Cola, vermelho vibrante com detalhes brancos..."
                className="w-full h-20 px-4 py-3 rounded-lg bg-white border border-tinta/15 text-sm text-tinta placeholder:text-tinta-soft/70 placeholder:italic resize-none focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    handleGenerate();
                  }
                }}
              />
              <div className="absolute bottom-2 right-3 pointer-events-none">
                <span className="text-xs text-tinta-soft/70 italic hidden sm:inline">⌘+Enter para enviar</span>
              </div>
            </div>

            {error && (
              <div className="flex gap-3 p-3 rounded-lg bg-bordo/8 border border-bordo/30">
                <AlertTriangle className="w-4 h-4 text-bordo mt-0.5 flex-shrink-0" />
                <p className="text-sm text-tinta">{error}</p>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={handleGenerate}
                disabled={!prompt.trim()}
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-tinta font-bold text-sm bg-mostarda hover:bg-mostarda-light border border-mostarda-dark/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Sparkles className="h-4 w-4" />
                Gerar Sugestões
              </button>
            </div>
          </>
        )}

        {/* Step: Loading */}
        {step === 'loading' && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-mostarda border border-tinta/20 flex items-center justify-center">
                <Palette className="h-8 w-8 text-tinta" />
              </div>
              <div className="absolute -inset-2 rounded-full border-2 border-bordo/20 border-t-bordo animate-spin" />
            </div>
            <div className="text-center">
              <h3 className="font-display text-lg font-bold text-tinta">Criando paletas de cores...</h3>
              <p className="text-sm text-tinta-soft italic mt-1">A IA está gerando 3 sugestões de temas</p>
            </div>
          </div>
        )}

        {/* Step: Preview */}
        {step === 'preview' && suggestions.length > 0 && (
          <>
            <div className="flex items-center gap-2 mb-1">
              <Palette className="h-4 w-4 text-tinta-soft" />
              <h4 className="text-xs font-bold uppercase tracking-wider text-tinta">
                {suggestions.length} Sugestão{suggestions.length > 1 ? 'ões' : ''} — clique para aplicar
              </h4>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {suggestions.map((theme, index) => (
                <button
                  key={index}
                  onClick={() => handleSelectTheme(theme)}
                  className="group rounded-xl border border-tinta/15 hover:border-bordo/40 overflow-hidden transition-all hover:shadow-md text-left bg-white"
                >
                  {/* Color preview — keeps user's theme colors */}
                  <div
                    className="h-24 p-4 flex items-center justify-center border-b border-tinta/10"
                    style={{
                      backgroundColor: theme.background_color,
                      color: theme.text_color,
                      fontFamily: theme.font_family,
                    }}
                  >
                    <div className="text-center">
                      <p className="text-lg font-bold">Roda de Prêmios</p>
                      <p className="text-xs opacity-80">Visualização do tema</p>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-3 flex items-center justify-between">
                    <div>
                      <h5 className="font-display text-sm font-bold text-tinta group-hover:text-bordo transition-colors">
                        {theme.name}
                      </h5>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="flex items-center gap-1 text-xs text-tinta-soft">
                          <span
                            className="w-3 h-3 rounded-sm border border-tinta/15"
                            style={{ backgroundColor: theme.background_color }}
                          />
                          <span className="font-mono">{theme.background_color}</span>
                        </span>
                        <span className="flex items-center gap-1 text-xs text-tinta-soft">
                          <span
                            className="w-3 h-3 rounded-sm border border-tinta/15"
                            style={{ backgroundColor: theme.text_color }}
                          />
                          <span className="font-mono">{theme.text_color}</span>
                        </span>
                        <span className="text-xs text-tinta-soft italic">{theme.font_family}</span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-bordo/8 text-bordo text-xs font-bold border border-bordo/20 group-hover:bg-bordo/15 transition-colors">
                      Aplicar
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between pt-2">
              <button
                onClick={() => { setStep('input'); setSuggestions([]); }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-tinta-soft bg-white border border-tinta/15 hover:bg-creme-light hover:text-tinta transition-colors"
              >
                <RotateCcw className="h-4 w-4" />
                Tentar novamente
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
